/**
 * The page library.
 *
 * Storage has been addressed by id since the server landed — `listDocs`, `getDoc`, `deleteDoc`
 * and version history all existed — but nothing in the UI ever showed more than the one
 * document the editor happened to open. So the tool could save many pages and reach exactly
 * one of them. This is that missing half.
 *
 * Every action that leaves the current page flushes the autosave first; see `openPage`.
 */

import { useEffect, useRef, useState } from 'react';
import type { CollctivDoc, DocType } from '@lp/shared';
import { FileText, Newspaper, Plus, Copy, Trash2, Upload, Download } from 'lucide-react';
import { useDocStore } from '../store/docStore.js';
import { api } from '../api/client.js';

/** "3 minutes ago" beats a timestamp for deciding which of two drafts is the live one. */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

export function PagesPanel(): React.ReactElement {
  const pages = useDocStore((s) => s.pages);
  const pagesLoading = useDocStore((s) => s.pagesLoading);
  const currentId = useDocStore((s) => s.id);
  const saveState = useDocStore((s) => s.saveState);
  const refreshPages = useDocStore((s) => s.refreshPages);
  const openPage = useDocStore((s) => s.openPage);
  const newPage = useDocStore((s) => s.newPage);
  const duplicatePage = useDocStore((s) => s.duplicatePage);
  const deletePage = useDocStore((s) => s.deletePage);
  const replaceDoc = useDocStore((s) => s.replaceDoc);
  const saveNow = useDocStore((s) => s.saveNow);

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Two-step delete. A page is an afternoon's work; one stray click must not end it. */
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // The list goes stale as soon as another tab saves, so refetch each time it is opened
  // rather than trusting whatever was loaded at boot.
  useEffect(() => {
    if (open) void refreshPages();
  }, [open, refreshPages]);

  const offline = saveState === 'offline';

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  async function importDoc(file: File): Promise<void> {
    const parsed = JSON.parse(await file.text()) as CollctivDoc;
    if (!parsed || typeof parsed !== 'object' || !parsed.puck) {
      throw new Error('That does not look like a page file.');
    }
    await saveNow();
    // A fresh id, so importing never overwrites the page the file came from.
    replaceDoc({ ...parsed, id: `doc-${Date.now().toString(36)}` });
    await saveNow('imported');
    await refreshPages();
  }

  if (!open) {
    return (
      <button
        type="button"
        className="lp-btn"
        onClick={() => setOpen(true)}
        title="All your saved pages"
      >
        Pages
        {pages.length > 0 && <span className="lp-btn__badge">{pages.length}</span>}
      </button>
    );
  }

  return (
    <div className="lp-export lpp">
      <div className="lp-export__head">
        <strong>Pages</strong>
        <button type="button" className="lp-btn lp-btn--ghost" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>

      {offline && (
        <p className="lpp__note">
          Not connected to the server, so this browser is the only copy. Saved pages will appear
          here once the server is reachable.
        </p>
      )}

      <div className="lpp__actions">
        <button
          type="button"
          className="lp-btn"
          disabled={busy || offline}
          onClick={() => void run(() => newPage('landing'))}
        >
          <Plus size={14} /> New landing page
        </button>
        <button
          type="button"
          className="lp-btn"
          disabled={busy || offline}
          onClick={() => void run(() => newPage('article'))}
        >
          <Plus size={14} /> New article
        </button>
        <button
          type="button"
          className="lp-btn"
          disabled={busy || offline}
          onClick={() => void run(duplicatePage)}
        >
          <Copy size={14} /> Duplicate this one
        </button>
        <button
          type="button"
          className="lp-btn"
          disabled={busy}
          onClick={() => fileInput.current?.click()}
        >
          <Upload size={14} /> Import a file
        </button>
        <input
          ref={fileInput}
          type="file"
          accept=".json,application/json"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void run(() => importDoc(file));
          }}
        />
      </div>

      {error && <p className="lpp__error">{error}</p>}

      {pagesLoading && <p className="lpp__note">Loading…</p>}

      {!pagesLoading && pages.length === 0 && !offline && (
        <p className="lpp__note">No pages saved yet. The one you are editing saves itself as you work.</p>
      )}

      <ul className="lpp__list">
        {pages.map((page) => {
          const isCurrent = page.id === currentId;
          const Icon = page.docType === 'article' ? Newspaper : FileText;
          return (
            <li key={page.id} className={`lpp__item${isCurrent ? ' lpp__item--current' : ''}`}>
              <button
                type="button"
                className="lpp__open"
                disabled={busy}
                onClick={() => void run(() => openPage(page.id))}
                title={isCurrent ? 'Already open' : 'Open this page'}
              >
                <Icon size={15} className="lpp__icon" aria-hidden />
                <span className="lpp__meta">
                  <span className="lpp__title">{page.title || 'Untitled page'}</span>
                  <span className="lpp__sub">
                    {page.slug ? `/${page.slug}` : 'no slug'} · {ago(page.updatedAt)}
                  </span>
                </span>
                {isCurrent && <span className="lpp__current">Open</span>}
              </button>

              <a
                className="lp-btn lp-btn--ghost lpp__icon-btn"
                href={api.exportUrl(page.id)}
                title="Download this page as a file"
              >
                <Download size={14} />
              </a>

              {confirmId === page.id ? (
                <span className="lpp__confirm">
                  <button
                    type="button"
                    className="lp-btn lp-btn--danger"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await deletePage(page.id);
                        setConfirmId(null);
                      })
                    }
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="lp-btn lp-btn--ghost"
                    onClick={() => setConfirmId(null)}
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="lp-btn lp-btn--ghost lpp__icon-btn"
                  disabled={busy}
                  onClick={() => setConfirmId(page.id)}
                  title="Delete this page"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
